import { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Video, ExternalLink, MapPin, CalendarPlus, ChevronDown, Copy, Share2, Sparkles, Crown, Users2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { NotificationToggle } from './NotificationToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import { API_URL, ensureProtocol } from '../config/api';

// Platform configuration for dynamic styling
const PLATFORM_CONFIG = {
  zoom: { label: 'Zoom', btnText: 'Unirse por Zoom', color: 'bg-[#2D8CFF] hover:bg-[#2681eb]', icon: Video },
  tiktok: { label: 'TikTok Live', btnText: 'Ver en TikTok', color: 'bg-[#010101] hover:bg-[#1a1a1a]', icon: Video },
  youtube: { label: 'YouTube Live', btnText: 'Ver en YouTube', color: 'bg-[#FF0000] hover:bg-[#e60000]', icon: Video },
  facebook: { label: 'Facebook Live', btnText: 'Ver en Facebook', color: 'bg-[#1877F2] hover:bg-[#166fe5]', icon: Video },
  instagram: { label: 'Instagram Live', btnText: 'Ver en Instagram', color: 'bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90', icon: Video },
  otro: { label: 'En Vivo', btnText: 'Ver Evento', color: 'bg-[#45A0EE] hover:bg-[#3590de]', icon: Video },
};

// Format time remaining for countdown
const formatTimeRemaining = (targetDate) => {
  const now = new Date();
  const diff = targetDate - now;
  
  if (diff <= 0) {
    return { expired: true, text: 'En vivo ahora!' };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  if (days > 0) {
    return { expired: false, days, hours, minutes, seconds };
  } else if (hours > 0) {
    return { expired: false, days: 0, hours, minutes, seconds };
  } else {
    return { expired: false, days: 0, hours: 0, minutes, seconds };
  }
};

// Calendar link generators
const generateCalendarLinks = (event, eventDateTime) => {
  const title = encodeURIComponent(event.title);
  const description = encodeURIComponent(
    `${event.description || ''}\n\nUnirse: ${event.zoom_link}`
  );
  const location = encodeURIComponent('Online');
  
  const startDate = eventDateTime;
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };
  
  const formatOutlookDate = (date) => {
    return date.toISOString().slice(0, 19);
  };
  
  const googleLink = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${description}&location=${location}`;
  const outlookLink = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${formatOutlookDate(startDate)}&enddt=${formatOutlookDate(endDate)}&body=${description}&location=${location}`;
  
  const generateICS = () => {
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Club Viajemos//Events//ES
BEGIN:VEVENT
UID:${event.id}@clubviajemos.com
DTSTAMP:${formatGoogleDate(new Date())}
DTSTART:${formatGoogleDate(startDate)}
DTEND:${formatGoogleDate(endDate)}
SUMMARY:${event.title}
DESCRIPTION:${event.description || ''} - Unirse: ${event.zoom_link}
LOCATION:Online
URL:${event.zoom_link}
END:VEVENT
END:VCALENDAR`;
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return { googleLink, outlookLink, generateICS };
};

// Convierte una hora "de pared" en una zona horaria dada al instante UTC correcto
const zonedWallTimeToDate = (dateStr, timeStr, timeZone) => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi, 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const map = {};
  dtf.formatToParts(new Date(utcGuess)).forEach((p) => { map[p.type] = p.value; });
  let hour = map.hour === '24' ? 0 : Number(map.hour);
  const asUTC = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), hour, Number(map.minute), Number(map.second));
  return new Date(utcGuess - (asUTC - utcGuess));
};

// Format date to user's timezone
const formatEventDateTime = (eventDate, eventTime, eventTimezone = 'America/New_York') => {
  try {
    // La hora del evento está definida en la zona del evento (ej. Miami/New_York).
    // La convertimos al instante UTC real para que la cuenta regresiva sea correcta
    // para cualquier visitante, sin importar su país.
    const eventDateTime = zonedWallTimeToDate(eventDate, eventTime, eventTimezone);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const dateOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: userTimezone
    };
    
    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: userTimezone
    };
    
    const formattedDate = eventDateTime.toLocaleDateString('es-ES', dateOptions);
    const formattedTime = eventDateTime.toLocaleTimeString('es-ES', timeOptions);
    
    return {
      date: formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1),
      time: formattedTime,
      dateTime: eventDateTime,
      userTimezone
    };
  } catch {
    return { date: eventDate, time: eventTime, dateTime: new Date(), userTimezone: 'Local' };
  }
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeRemaining, setTimeRemaining] = useState(formatTimeRemaining(targetDate));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(formatTimeRemaining(targetDate));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [targetDate]);
  
  if (timeRemaining.expired) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full animate-pulse">
        <span className="w-2 h-2 bg-white rounded-full animate-ping" />
        <span className="text-sm font-bold">{timeRemaining.text}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      {timeRemaining.days > 0 && (
        <div className="flex flex-col items-center bg-[#45A0EE]/10 px-3 py-2 rounded-lg">
          <span className="text-lg font-bold text-[#45A0EE]">{timeRemaining.days}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">dias</span>
        </div>
      )}
      <div className="flex flex-col items-center bg-[#45A0EE]/10 px-3 py-2 rounded-lg">
        <span className="text-lg font-bold text-[#45A0EE]">{String(timeRemaining.hours).padStart(2, '0')}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">hrs</span>
      </div>
      <span className="text-xl font-bold text-[#45A0EE]">:</span>
      <div className="flex flex-col items-center bg-[#45A0EE]/10 px-3 py-2 rounded-lg">
        <span className="text-lg font-bold text-[#45A0EE]">{String(timeRemaining.minutes).padStart(2, '0')}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">min</span>
      </div>
      <span className="text-xl font-bold text-[#45A0EE]">:</span>
      <div className="flex flex-col items-center bg-[#80C753]/10 px-3 py-2 rounded-lg">
        <span className="text-lg font-bold text-[#80C753]">{String(timeRemaining.seconds).padStart(2, '0')}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">seg</span>
      </div>
    </div>
  );
};

// Single Event Card
const EventCard = ({ event, isNext, featured = false, pageLocation = 'home' }) => {
  const { date, time, dateTime, userTimezone } = formatEventDateTime(
    event.event_date, 
    event.event_time,
    event.timezone
  );
  
  const isPast = dateTime < new Date();
  
  const openLink = (url) => {
    const fullUrl = ensureProtocol(url);
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const shareUrl = ensureProtocol(event.zoom_link);
  const platformLabel = (PLATFORM_CONFIG[event.platform] || PLATFORM_CONFIG.otro).label;

  // Nombre del día del evento, en su propia zona horaria, para personalizar el mensaje
  const eventWeekday = dateTime
    .toLocaleDateString('es-ES', { weekday: 'long', timeZone: event.timezone || 'America/New_York' })
    .toUpperCase();

  // Horario del evento en las principales zonas de USA (calculado desde la hora real del evento)
  const usZones = [
    { flag: '🇺🇸', label: 'Miami / NY', tz: 'America/New_York' },
    { flag: '🇺🇸', label: 'Texas / Chicago', tz: 'America/Chicago' },
    { flag: '🇺🇸', label: 'California / LA', tz: 'America/Los_Angeles' },
  ];
  const fmtZoneTime = (tz) =>
    dateTime
      .toLocaleTimeString('es-ES', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz })
      .replace(/\s*a\.?\s*m\.?/i, ' AM')
      .replace(/\s*p\.?\s*m\.?/i, ' PM')
      .trim();

  // Mensaje de compartir personalizado para el día propio de cada evento
  const shareCaption =
    `🎟️ CUPO SEPARADO PARA ESTE ${eventWeekday}\n\n` +
    `📌 ${event.title}\n\n` +
    `⏰ Horarios de transmisión en USA:\n` +
    usZones.map((z) => `${z.flag} ${fmtZoneTime(z.tz)} (${z.label})`).join('\n') + `\n\n` +
    `Para asegurar tu lugar y no quedarte fuera por olvido, este es tu enlace directo para entrar a la sala de Zoom:\n\n` +
    `🚀 Enlace directo:\n${shareUrl}\n\n` +
    `¡Nos vemos en vivo! Recuerda conectarte 5 minutos antes.`;

  // Solo el evento de líderes de los lunes adjunta el flyer como vista previa
  const isMondayLeaders =
    event.recurrence === 'weekly_mon' || /L[IÍ]DERES/i.test(event.title || '');

  // En /educacion ocultar el botón "Invitar a un invitado" para martes, miércoles y jueves
  const hideInviteEducacion =
    pageLocation === 'educacion' &&
    ['weekly_tue', 'weekly_wed', 'weekly_thu'].includes(event.recurrence);

  const shareMessage = isMondayLeaders
    ? `https://www.clubviajemos.com/lunes-exito.png\n\n${shareCaption}`
    : shareCaption;

  // Mensaje de invitación (para traer invitados a cualquier evento)
  const inviteCaption = isMondayLeaders
    ? (
      `🌟 Te invito a un entrenamiento que puede cambiar tu manera de generar ingresos 🌟\n\n` +
      `📌 ${event.title}\n\n` +
      `Cada lunes, el CEO de Club Viajemos, Carlos "Miami" Fernández, se sienta EN VIVO para entrenarnos directamente en cómo ganar dinero en la industria de los viajes con los 5 Secretos Vacacionales. Él alcanzó el éxito SIN experiencia previa en el turismo… y hoy comparte ese camino contigo.\n\n` +
      `⏰ Horarios de transmisión en USA:\n` +
      usZones.map((z) => `${z.flag} ${fmtZoneTime(z.tz)} (${z.label})`).join('\n') + `\n\n` +
      `No necesitas experiencia, solo ganas de crecer. Este es tu enlace directo para entrar a la sala de Zoom:\n\n` +
      `🚀 ${shareUrl}\n\n` +
      `¡Te espero en el entrenamiento! Conéctate 5 minutos antes. 🙌`
    )
    : (
      `✨ ¡Te invito a un evento EN VIVO de Club Viajemos! ✨\n\n` +
      `📌 ${event.title}\n\n` +
      (event.description ? `${event.description}\n\n` : '') +
      `⏰ Horarios de transmisión en USA:\n` +
      usZones.map((z) => `${z.flag} ${fmtZoneTime(z.tz)} (${z.label})`).join('\n') + `\n\n` +
      `Este es tu enlace directo para entrar a la sala de Zoom:\n\n` +
      `🚀 ${shareUrl}\n\n` +
      `¡Te esperamos en vivo! Conéctate 5 minutos antes. 🙌`
    );

  const inviteMessage = isMondayLeaders
    ? `https://www.clubviajemos.com/lunes-exito.png\n\n${inviteCaption}`
    : inviteCaption;

  const copyLink = async () => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
        ok = true;
      }
    } catch (e) {
      ok = false;
    }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) {
        ok = false;
      }
    }
    if (ok) {
      toast.success('¡Enlace copiado! Ya puedes compartirlo.');
    } else {
      toast.error('Copia manualmente este enlace: ' + shareUrl);
    }
  };

  const shareWhatsApp = async () => {
    // Reunión de lunes: intenta compartir el FLYER como imagen real (Web Share API, móvil)
    if (isMondayLeaders && typeof navigator !== 'undefined' && navigator.canShare) {
      try {
        const res = await fetch('/lunes-exito.png');
        const blob = await res.blob();
        const file = new File([blob], 'lunes-exito.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: shareCaption });
          return;
        }
      } catch (e) {
        // si el usuario cancela o no hay soporte, cae al fallback
        if (e && e.name === 'AbortError') return;
      }
    }
    openLink(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`);
  };

  const inviteWhatsApp = async () => {
    // El entrenamiento del lunes intenta compartir el FLYER como imagen real (Web Share API, móvil)
    if (isMondayLeaders && typeof navigator !== 'undefined' && navigator.canShare) {
      try {
        const res = await fetch('/lunes-exito.png');
        const blob = await res.blob();
        const file = new File([blob], 'lunes-exito.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: inviteCaption });
          return;
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    openLink(`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`);
  };

  const { googleLink, outlookLink, generateICS } = generateCalendarLinks(event, dateTime);

  // Modo de botones: "whatsapp" => solo un botón de WhatsApp; cualquier otro => todos los botones
  const whatsappOnly = event.cta_mode === 'whatsapp';
  const waNumber = (event.whatsapp_number || '19548059212').replace(/[^0-9]/g, '');
  const waMessage =
    `Hola 👋, quiero más información sobre el evento:\n` +
    `📌 ${event.title}\n` +
    `📅 ${date} · ⏰ ${time}`;
  const openWhatsAppContact = () => {
    openLink(`https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`);
  };

  // Aviso temporal SOLO para el webinar de lunes, hasta la medianoche de hoy (hora Miami)
  const noticeActive = isMondayLeaders && Date.now() < Date.parse('2026-08-25T04:00:00Z');

  return (
    <div 
      data-testid={`event-card-${event.id}`}
      className={`relative rounded-2xl border overflow-hidden transition-all duration-300 flex flex-col ${
        featured
          ? 'bg-gradient-to-br from-[#0A1E3F] via-[#10294F] to-[#0A1E3F] border-2 border-[#E3C766] shadow-2xl shadow-[#E3C766]/20'
          : `bg-white dark:bg-[#151B2B] ${
              isPast
                ? 'border-slate-200 dark:border-slate-700 opacity-75'
                : isNext
                ? 'border-[#45A0EE] shadow-xl shadow-[#45A0EE]/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-[#45A0EE]/50'
            }`
      }`}
    >
      {/* Cinta de evento central */}
      {featured && !isPast && (
        <div className="absolute top-0 left-0 z-20 bg-gradient-to-r from-[#E3C766] to-[#c9a84c] text-[#0A1E3F] text-[11px] font-black uppercase tracking-wider px-4 py-1.5 rounded-br-xl flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" />
          Evento central de entrenamiento
        </div>
      )}
      {/* Aviso temporal: webinar postergado (solo hoy hasta medianoche) */}
      {noticeActive && (
        <div
          data-testid="monday-postponed-notice"
          className="absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-[#0A1E3F]/[0.97] via-[#10294F]/[0.97] to-[#0A1E3F]/[0.97] backdrop-blur-sm"
        >
          <Calendar className="h-10 w-10 text-[#E3C766] mb-3" />
          <h3 className="text-xl sm:text-2xl font-black text-white leading-tight mb-2">
            WEBINAR POSTERGADO AL MIÉRCOLES
          </h3>
          <p className="text-base text-[#E3C766] font-semibold">
            Carlos está viajando, ¡nos vemos! 🚀
          </p>
        </div>
      )}
      {/* Event Badge */}
      {isPast ? (
        <div className="absolute top-0 right-0 bg-slate-400 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
          FINALIZADO
        </div>
      ) : isNext && (
        <div className="absolute top-0 right-0 bg-gradient-to-l from-[#45A0EE] to-[#80C753] text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
          PROXIMO
        </div>
      )}
      
      <div className={`p-6 flex flex-col flex-1 ${featured ? 'pt-11' : ''}`}>
        {/* Platform Badge */}
        {(() => {
          const p = event.platform || 'zoom';
          const cfg = PLATFORM_CONFIG[p] || PLATFORM_CONFIG.otro;
          return (
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider w-fit px-2 py-0.5 rounded-full mb-2 ${
              featured ? 'bg-[#2D8CFF]/20 text-[#8ec3ff]' :
              p === 'zoom' ? 'bg-[#2D8CFF]/10 text-[#2D8CFF]' :
              p === 'tiktok' ? 'bg-black/10 text-black dark:bg-white/10 dark:text-white' :
              p === 'youtube' ? 'bg-red-500/10 text-red-600' :
              p === 'facebook' ? 'bg-[#1877F2]/10 text-[#1877F2]' :
              p === 'instagram' ? 'bg-pink-500/10 text-pink-600' :
              'bg-[#45A0EE]/10 text-[#45A0EE]'
            }`}>
              <Video className="h-3 w-3" />
              {cfg.label}
            </span>
          );
        })()}

        {/* CEO badge (solo evento central) */}
        {featured && (
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 bg-[#E3C766]/15 border border-[#E3C766]/40 text-[#E3C766] text-xs font-bold px-3 py-1 rounded-full">
              <Crown className="h-3.5 w-3.5" />
              Con el CEO Carlos "Miami" Fernández
            </span>
          </div>
        )}

        {/* Event Title */}
        <h3 className={`font-bold mb-2 pr-16 ${featured ? 'text-white text-2xl leading-tight' : 'text-xl text-[#1D1D1B] dark:text-white'}`}>
          {event.title}
        </h3>
        
        {/* Description */}
        {event.description && (
          <p className={`mb-4 ${featured ? 'text-slate-200 text-[15px] leading-relaxed' : 'text-sm text-slate-600 dark:text-slate-400'}`}>
            {event.description}
          </p>
        )}
        
        {/* Date & Time Info */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-[#45A0EE]" />
            <span className={featured ? 'text-slate-100' : 'text-slate-700 dark:text-slate-300'}>{date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-[#80C753]" />
            <span className={featured ? 'text-slate-100' : 'text-slate-700 dark:text-slate-300'}>{time}</span>
          </div>
          <div className={`flex items-center gap-2 text-xs ${featured ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
            <MapPin className="h-3 w-3" />
            <span>Tu zona: {userTimezone}</span>
          </div>
        </div>
        
        {/* Countdown Timer */}
        <div className="mb-4">
          <p className={`text-xs mb-2 ${featured ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>Tiempo restante:</p>
          <CountdownTimer targetDate={dateTime} />
        </div>
        
        {/* Buttons Container - Push to bottom */}
        <div className="mt-auto space-y-2.5">
          {whatsappOnly ? (
            /* Modo WhatsApp: un solo botón */
            <button
              type="button"
              data-testid={`whatsapp-only-${event.id}`}
              onClick={openWhatsAppContact}
              className="group/waonly w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] text-white font-bold text-sm shadow-lg shadow-[#25D366]/25 transition-all duration-200 hover:bg-[#1eb857] hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#25D366]/40 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
              title="Escríbenos por WhatsApp"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 transition-transform duration-200 group-hover/waonly:scale-110"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Escríbenos por WhatsApp
            </button>
          ) : (
          <>
          {/* Main Buttons Row */}
          <div className="flex gap-2">
            {/* Main Platform Button */}
            {(() => {
              const platform = PLATFORM_CONFIG[event.platform] || PLATFORM_CONFIG.otro;
              const PlatformIcon = platform.icon;
              return (
                <button
                  type="button"
                  data-testid={`join-event-${event.id}`}
                  onClick={() => openLink(event.zoom_link)}
                  className={`group/join relative flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-xl ${platform.color} text-white font-bold text-sm shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70`}
                >
                  {!isPast && (
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                    </span>
                  )}
                  <PlatformIcon className="h-4 w-4" />
                  {platform.btnText}
                  <ExternalLink className="h-4 w-4 transition-transform duration-200 group-hover/join:translate-x-0.5 group-hover/join:-translate-y-0.5" />
                </button>
              );
            })()}
            
            {/* Add to Calendar Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid={`calendar-${event.id}`}
                  title="Agregar a tu calendario"
                  className="inline-flex items-center justify-center gap-1 h-12 px-3.5 rounded-xl border-2 border-[#80C753]/70 text-[#80C753] font-bold transition-all duration-200 hover:bg-[#80C753] hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#80C753]/30 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#80C753]"
                >
                  <CalendarPlus className="h-4 w-4" />
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl">
                <DropdownMenuItem 
                  onClick={() => openLink(googleLink)}
                  className="cursor-pointer rounded-lg font-medium"
                >
                  Google Calendar
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => openLink(outlookLink)}
                  className="cursor-pointer rounded-lg font-medium"
                >
                  Outlook
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={generateICS}
                  className="cursor-pointer rounded-lg font-medium"
                >
                  Apple Calendar (.ics)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Share / Copy Link Row */}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={`copy-link-${event.id}`}
              onClick={copyLink}
              className="group/copy flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-[#2D8CFF]/50 text-[#2D8CFF] font-bold text-sm transition-all duration-200 hover:bg-[#2D8CFF] hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#2D8CFF]/30 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D8CFF]"
            >
              <Copy className="h-4 w-4 transition-transform duration-200 group-hover/copy:scale-110" />
              Copiar enlace
            </button>
            <button
              type="button"
              data-testid={`whatsapp-share-${event.id}`}
              onClick={shareWhatsApp}
              className="group/wa inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl bg-[#25D366] text-white font-bold text-sm shadow-lg shadow-[#25D366]/25 transition-all duration-200 hover:bg-[#1eb857] hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#25D366]/40 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
              title="Compartir por WhatsApp"
            >
              <Share2 className="h-4 w-4 transition-transform duration-200 group-hover/wa:rotate-12" />
              <span className="hidden sm:inline">Compartir</span>
            </button>
          </div>

          {/* Botón Invitar — todos los eventos excepto el entrenamiento del lunes (y oculto en /educacion para mar/mié/jue) */}
          {!isMondayLeaders && !hideInviteEducacion && (
            <button
              type="button"
              data-testid={`invite-guests-${event.id}`}
              onClick={inviteWhatsApp}
              className="group/inv w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-[#C9A84C] to-[#e6c766] text-[#0A1E3F] font-black text-sm shadow-lg shadow-[#C9A84C]/25 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#C9A84C]/40 hover:brightness-105 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E3C766]"
              title="Invitar a un invitado al evento"
            >
              <Users2 className="h-4 w-4 transition-transform duration-200 group-hover/inv:scale-110" />
              Invitar a un invitado
            </button>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

const EVENTS_CACHE_KEY = 'clubviajemos_events_cache';

const getCachedEvents = (location = 'home') => {
  try {
    const cached = localStorage.getItem(`${EVENTS_CACHE_KEY}_${location}`);
    if (cached) {
      const { events, timestamp } = JSON.parse(cached);
      // Cache valid for 24 hours
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000 && Array.isArray(events)) {
        return events;
      }
    }
  } catch (e) {
    console.error('Failed to read events cache:', e);
  }
  return null;
};

const setCachedEvents = (events, location = 'home') => {
  try {
    localStorage.setItem(`${EVENTS_CACHE_KEY}_${location}`, JSON.stringify({ events, timestamp: Date.now() }));
  } catch (e) {
    console.error('Failed to cache events:', e);
  }
};

const sortEvents = (data) => {
  const now = new Date();
  return [...data].sort((a, b) => {
    const dateA = zonedWallTimeToDate(a.event_date, a.event_time, a.timezone || 'America/New_York');
    const dateB = zonedWallTimeToDate(b.event_date, b.event_time, b.timezone || 'America/New_York');
    const aFuture = dateA > now ? 0 : 1;
    const bFuture = dateB > now ? 0 : 1;
    if (aFuture !== bFuture) return aFuture - bFuture;
    return dateA - dateB;
  }).slice(0, 6);
};

export const EventsSection = ({ location = 'home', title = 'Proximos Eventos', subtitle = 'Webinars y capacitaciones para agentes' }) => {
  // Load cached events immediately so they never flash empty
  const [events, setEvents] = useState(() => {
    const cached = getCachedEvents(location);
    return cached ? sortEvents(cached) : [];
  });
  const [status, setStatus] = useState(() => getCachedEvents(location) ? 'ok' : 'loading');

  const loadEvents = useCallback(() => {
    fetch(`${API_URL}/api/events?location=${location}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
          setEvents([]);
          setStatus('ok');
          return;
        }
        const sorted = sortEvents(data);
        setEvents(sorted);
        setCachedEvents(data, location);
        setStatus('ok');
      })
      .catch(() => {
        const cached = getCachedEvents(location);
        if (cached && cached.length > 0) {
          setEvents(sortEvents(cached));
          setStatus('ok');
        } else {
          setStatus('error');
        }
      });
  }, [location]);

  useEffect(() => {
    loadEvents();
    const interval = setInterval(loadEvents, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadEvents]);
  
  if (status === 'loading' && events.length === 0) {
    return (
      <section data-testid="events-section" className="mb-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#45A0EE]/10 rounded-xl">
            <Calendar className="h-6 w-6 text-[#45A0EE]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1D1D1B] dark:text-white">Proximos Eventos</h2>
            <p className="text-sm text-slate-500">Cargando...</p>
          </div>
        </div>
      </section>
    );
  }
  
  return (
    <section 
      data-testid="events-section"
      className="mb-10"
    >
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#45A0EE] to-[#80C753]">
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1D1D1B] dark:text-white">
              {title}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>
        </div>
        
        {/* Notification Toggle Button */}
        <NotificationToggle events={events} />
      </div>
      
      {/* Events Grid */}
      {status === 'error' ? (
        <div className="text-center py-10 bg-white dark:bg-[#151B2B] rounded-2xl border border-red-200 dark:border-red-800">
          <Calendar className="h-12 w-12 text-red-300 mx-auto mb-3" />
          <p className="text-base font-medium text-red-500">Error al cargar eventos</p>
          <p className="text-sm text-slate-400 mt-1">Revisa tu conexion a internet</p>
          <Button
            data-testid="retry-events-btn"
            onClick={() => loadEvents(false)}
            variant="outline"
            size="sm"
            className="mt-4 border-red-300 text-red-500 hover:bg-red-50"
          >
            Reintentar
          </Button>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-10 bg-white dark:bg-[#151B2B] rounded-2xl border border-slate-200 dark:border-slate-700">
          <Calendar className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-base font-medium text-slate-500 dark:text-slate-400">No hay eventos programados</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Los nuevos eventos apareceran aqui automaticamente</p>
          <Button
            data-testid="retry-events-btn"
            onClick={() => loadEvents(false)}
            variant="outline"
            size="sm"
            className="mt-4 border-[#45A0EE] text-[#45A0EE] hover:bg-[#45A0EE]/10"
          >
            Recargar
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, index) => {
            const eventDt = zonedWallTimeToDate(event.event_date, event.event_time, event.timezone || 'America/New_York');
            const isFutureEvent = eventDt > new Date();
            const isFirstFuture = isFutureEvent && !events.slice(0, index).some(e =>
              zonedWallTimeToDate(e.event_date, e.event_time, e.timezone || 'America/New_York') > new Date()
            );
            return (
              <EventCard
                key={event.id}
                event={event}
                isNext={isFirstFuture}
                pageLocation={location}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};

export default EventsSection;
